import { Loader } from '@/components'
import { config } from '@/config'
import { AssessmentStepKeys, Categories } from '@/enums'
import {
  partnerFunnelCategories,
  validPartners,
} from '@/modules/partner/partnerConfig'
import { useAssessment, useSelfAssessment } from '@/services'
import { goToExternalUrl, safeTimestamp } from '@/utils'
import { omit } from 'ramda'
import { useMemo } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { makeBusinessTypeSubmitValue } from '../helpers'
import { PartnerAssessment } from './BasePage'
import { makeSteps } from './helpers'
import { MySelfAssessment, GetSelfAssessmentApiResponse } from '@/api'

interface Company {
  company_id: string
  locations: string[]
  poaSigned?: boolean
}

interface OutletContext {
  user: {
    companies: Company[]
  }
}

interface InitialValues {
  businessType: any
  companyFoundedDateLead: {
    foundedDate: number | null
  }
  approxRevenue: {
    approxTurnover?: number | null
  }
  employeesCount: any
  costOfElectronics: {
    approximateCostOfElectroEquipment?: number | null
  }
  approxLaborCosts: {
    approxPersonelExpenses?: number | null
  }
  companyDetails: any
  userProfile: any
  inventory: any
  locationDetails?: any
}

interface SubmitValuesParams {
  approxRevenue: any
  approxLaborCosts: any
  businessType: any
  companyFoundedDateLead: any
  inventory?: any
  costOfElectronics: any
  [key: string]: any
}

interface StepMessages {
  helperText: string
}

const findLocationId = (data?: MySelfAssessment): string => {
  if (data?.companies.length && data.companies.length > 0) {
    return data.companies[0].locations[0]
  }
  return ''
}

const checkPoaSigned = (companies: Company[]): boolean => {
  const poaSigned = companies[0]?.poaSigned ?? false

  return poaSigned
}

const makeInitialValues = (
  assessmentData: GetSelfAssessmentApiResponse,
  categoryId: string,
): InitialValues => {
  const { values } = assessmentData
  const foundedDate = values.companyFoundedDate?.foundedDate

  const initialValues: InitialValues = {
    businessType: {
      ...values.businessType,
      locationType:
        values.businessType?.locationType === 'other'
          ? ''
          : values.businessType?.locationType,
    },
    companyFoundedDateLead: {
      foundedDate: foundedDate ? safeTimestamp(foundedDate) : null,
    },
    approxRevenue: {
      approxTurnover: values.lastYearFinances?.approxTurnover ?? undefined,
    },
    employeesCount: {
      ...values.employeesCount,
    },
    costOfElectronics: {
      approximateCostOfElectroEquipment:
        values.lastYearFinances?.approximateCostOfElectroEquipment ?? undefined,
    },
    approxLaborCosts: {
      approxPersonelExpenses:
        values.lastYearFinances?.approxPersonelExpenses ?? undefined,
    },
    companyDetails: {
      ...values.companyDetails,
    },
    userProfile: {
      //@ts-expect-error TODO fix later
      ...values.userProfile,
    },
    inventory: {
      ...values.locationRent,
    },
  }

  // Only CO requires locationDetails
  if (categoryId === 'CO') {
    initialValues.locationDetails = {
      ...values.locationDetails,
    }
  }

  return initialValues
}

const makeSubmitValues = () => (
  {
    approxRevenue,
    approxLaborCosts,
    businessType,
    companyFoundedDateLead,
    inventory = {},
    costOfElectronics,
    ...steps
  }: SubmitValuesParams,
  poaSigned = false,
) => {
  if (poaSigned) {
    const stepsWithoutDetailsOrProfile = omit(
      ['companyDetails', 'userProfile'],
      steps,
    )

    return {
      ...stepsWithoutDetailsOrProfile,
      lastYearFinances: {
        ...approxLaborCosts,
        ...approxRevenue,
        ...costOfElectronics,
      },
      locationRent: { ...inventory },
      businessType: makeBusinessTypeSubmitValue(businessType),
      companyFoundedDate: companyFoundedDateLead,
      prepareData: {},
    }
  }

  return {
    ...steps,
    lastYearFinances: { ...approxLaborCosts, ...approxRevenue },
    locationRent: { ...inventory },
    businessType: makeBusinessTypeSubmitValue(businessType),
    companyFoundedDate: companyFoundedDateLead,
    prepareData: {},
  }
}

const makeStepProps = () => (messages: StepMessages) => ({
  [AssessmentStepKeys.approxRevenue]: {
    helperText: messages.helperText,
  },
  [AssessmentStepKeys.approxLaborCosts]: {
    helperText: messages.helperText,
  },
})

export const PartnerAssessmentPage = () => {
  const { user } = useOutletContext<OutletContext>()
  const poaSigned = checkPoaSigned(user.companies)
  const { partnerId, categoryId } = useParams<{
    partnerId: string
    categoryId: keyof typeof Categories
  }>() as { partnerId: string; categoryId: keyof typeof Categories }

  const isValidCategory = partnerFunnelCategories.includes(categoryId)
  const isValidPartner = validPartners.includes(partnerId)

  const steps = useMemo(() => makeSteps(categoryId, poaSigned), [
    categoryId,
    poaSigned,
  ])

  const {
    data: selfAssessmentData,
    isLoading: isSelfAssessmentLoading,
  } = useSelfAssessment({ enabled: true })
  const locationId = findLocationId(selfAssessmentData)
  const {
    data: assessmentData,
    isLoading: isAssessmentLoading,
  } = useAssessment(locationId, {
    enabled: !!locationId,
  })

  if (!isValidPartner || !isValidCategory) {
    goToExternalUrl(config.appUrl)
  }

  if (
    isSelfAssessmentLoading ||
    isAssessmentLoading ||
    !assessmentData ||
    !locationId
  ) {
    return <Loader />
  }

  return (
    <PartnerAssessment
      initialValues={makeInitialValues(assessmentData, categoryId)}
      locationId={locationId}
      partnerSource={partnerId}
      categoryId={categoryId}
      poaSigned={poaSigned}
      makeStepProps={makeStepProps()}
      stepKeys={steps}
      transformSubmitValues={makeSubmitValues()}
    />
  )
}
